/** Default chart height (px) for sidebar trend-curve graphs. */
export const TREND_CURVE_CHART_HEIGHT = 280;

/** Chart height (px) for expanded view (report / screenshot friendly). */
export const TREND_CURVE_EXPANDED_CHART_HEIGHT = 400;

/** Fallback / PDF chart plot width (px); live expanded view fills available space. */
export const TREND_CURVE_EXPANDED_CHART_WIDTH = 1600;

/**
 * Content-based width targets (capped by container).
 * Sparse series also enforce a min fraction of available width so the chart
 * stays connected to the full-width table below (~55–75% for 2–4 months).
 */
export const TREND_CURVE_PX_PER_PERIOD_COMPACT = 95;
export const TREND_CURVE_PX_PER_PERIOD_EXPANDED = 180;

export const TREND_CURVE_MIN_CHART_WIDTH_COMPACT = 300;
export const TREND_CURVE_MIN_CHART_WIDTH_EXPANDED = 700;

export const TREND_CURVE_WIDTH_MARGIN_COMPACT = 90;
export const TREND_CURVE_WIDTH_MARGIN_EXPANDED = 140;

/** Max trends shown in the on-chart legend (report / screenshot). */
export const TREND_CURVE_LEGEND_MAX_ITEMS = 10;

/** Side legend box width (px) in expanded / PDF layout. */
export const TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED = 320;

/** Side legend box width (px) in sidebar layout. */
export const TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT = 148;

/**
 * Pleasant, high-contrast line colours for trend curves.
 * Avoids alarm reds and muddy browns so multiple series stay readable.
 */
export const TREND_CURVE_LINE_COLORS = [
  "#2563eb",
  "#0891b2",
  "#059669",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#4f46e5",
  "#0d9488",
  "#65a30d",
  "#c026d3",
  "#0284c8",
  "#ca8a04",
  "#9333ea",
  "#14b8a6",
  "#f59e0b",
  "#6366f1",
  "#10b981",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#3b82f6",
  "#84cc16",
  "#a855f7",
  "#22c55e",
  "#f97316",
  "#0ea5e9",
  "#d946ef",
  "#2dd4bf",
  "#eab308",
  "#818cf8",
  "#34d399",
  "#f472b6",
  "#38bdf8",
  "#a3e635",
  "#c084fc",
  "#fb923c",
  "#5eead4",
  "#fbbf24",
  "#67e8f9",
  "#86efac",
  "#fda4af",
  "#93c5fd",
  "#d8b4fe",
  "#fdba74",
  "#6ee7b7",
  "#fde047",
  "#a5b4fc",
  "#7dd3fc",
  "#bef264",
  "#e879f9",
] as const;

export const TREND_CURVE_DISTINCT_COLOR_COUNT = TREND_CURVE_LINE_COLORS.length;

/** One unique colour per trend index (cycles through {@link TREND_CURVE_LINE_COLORS}). */
export const getTrendCurveLineColor = (_trendName: string, index: number) => {
  const slot =
    ((index % TREND_CURVE_LINE_COLORS.length) +
      TREND_CURVE_LINE_COLORS.length) %
    TREND_CURVE_LINE_COLORS.length;
  return TREND_CURVE_LINE_COLORS[slot];
};

/** Display labels for quarterly periods (Q1–Q4). */
export const TREND_CURVE_QUARTER_RANGES = [
  "Jan–Mar",
  "Apr–Jun",
  "Jul–Sep",
  "Oct–Dec",
] as const;

/** Matches backend quarter period labels, e.g. "Q1 2026". */
export const TREND_CURVE_QUARTER_PERIOD_REGEX = /^Q(\d)\s+(\d{4})$/;

/** Matches backend half-year period labels, e.g. "H1 2026". */
export const TREND_CURVE_HALF_YEAR_PERIOD_REGEX = /^H([12])\s+(\d{4})$/;

/** Matches backend year period labels, e.g. "2026". */
export const TREND_CURVE_YEAR_PERIOD_REGEX = /^(\d{4})$/;
